import { CANVAS_SIZE } from '@/constant';
import { ShortcutAction, useShortcut } from '@/hooks/useShortcut/useShortcut';
import { useSlides } from '@/hooks/useSlides/useSlides';
import { useUndoManager } from '@/hooks/useUndoManager/useUndoManager';
import { SelectedSlideType } from '@/pages/LessonEditor/LessonEditor.page';
import { Coordinate, isSlideComponent, isSlideComponentText, parseSlide, Size, SlideComponent, SlideComponentFileModel, Slide as SlideData } from '@/types/slide';
import { useApolloClient } from '@apollo/client';
import { isFunction } from '@mui/x-data-grid/internals';
import React, { useCallback, useEffect, useState } from 'react';
import { CreateSlideComponent } from './creators/factory';
import { SlideComponentAudioCreator } from './creators/SlideComponentAudio.creator';
import { SlideComponentAudioRecordCreator } from './creators/SlideComponentAudioRecord.creator';
import { SlideComponentBackgroundCreator } from './creators/SlideComponentBackground.creator';
import { SlideComponentCharacterCreator } from './creators/SlideComponentCharacter.creator';
import { SlideComponentCharacterCardsCreator } from './creators/SlideComponentCharacterCards.creator';
import { SlideComponentCharacterWritingCreator } from './creators/SlideComponentCharacterWriting.creator';
import { SlideComponentDndCreator } from './creators/SlideComponentDnd.creator';
import { SlideComponentDrawingCreator } from './creators/SlideComponentDrawing.creator';
import { SlideComponentHkcsEnQ1Creator } from './creators/SlideComponentHkcsEnQ1.creator';
import { SlideComponentHkcsEnQ2Creator } from './creators/SlideComponentHkcsEnQ2.creator';
import { SlideComponentHkcsQ1Creator } from './creators/SlideComponentHkcsQ1.creator';
import { SlideComponentHkcsQ2Creator } from './creators/SlideComponentHkcsQ2.creator';
import { SlideComponentHkcsQ3Creator } from './creators/SlideComponentHkcsQ3.creator';
import { SlideComponentImageCreator } from './creators/SlideComponentImage.creator';
import { SlideComponentLinkCreator } from './creators/SlideComponentLink.creator';
import { SlideComponentMCCreator } from './creators/SlideComponentMC.creator';
import { SlideComponentPronunciationAssessmentCreator } from './creators/SlideComponentPronunciationAssessment.creator';
import { SlideComponentRadicalWritingCreator } from './creators/SlideComponentRadicalWriting.creator';
import { SlideComponentSentenceMakerCreator } from './creators/SlideComponentSentenceMaker.creator';
import { SlideComponentStrokeWritingCreator } from './creators/SlideComponentStrokeWriting.creator';
import { SlideComponentTextCreator } from './creators/SlideComponentText.creator';
import { GET_SLIDES, GetSlidesResponse } from './gql';

const APP_CLIPBOARD_MIME = 'text/html';

const createData = (prev: SlideData[], componentId: string, componentType: string, coordinate: Coordinate, selectedIndex: number) => {
    if (!prev || !prev[selectedIndex]) {
        return prev;
    }

    let newSlideData = prev;
    switch (componentType) {
        case 'text':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentTextCreator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 200, height: 50 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'background':
            // allow only one background component
            if (prev[selectedIndex].data.some((c) => c.type === 'background')) {
                return prev;
            }

            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentBackgroundCreator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, CANVAS_SIZE),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'image':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentImageCreator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 200, height: 200 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'audio':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentAudioCreator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 100, height: 100 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'character':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentCharacterCreator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 200, height: 200 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'dnd':
            // allow only one dnd component
            if (prev[selectedIndex].data.some((c) => c.type === 'dnd')) {
                return prev;
            }

            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentDndCreator(), componentId),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'characterWriting':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentCharacterWritingCreator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 400, height: 400 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'characterCards':
            // allow only one character card component
            if (prev[selectedIndex].data.some((c) => c.type === 'characterCards')) {
                return prev;
            }
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,

                            {
                                ...CreateSlideComponent(new SlideComponentCharacterCardsCreator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 644, height: 468 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'drawing':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentDrawingCreator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 400, height: 300 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'sentenceMaker':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentSentenceMakerCreator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 800, height: 448 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'hkcsQ1':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentHkcsQ1Creator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 200, height: 200 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'hkcsQ2':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentHkcsQ2Creator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 200, height: 200 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;

        case 'hkcsQ3':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentHkcsQ3Creator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 200, height: 200 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'hkcsEnQ1':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentHkcsEnQ1Creator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 200, height: 200 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;

        case 'hkcsEnQ2':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentHkcsEnQ2Creator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 200, height: 200 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'audioRecord':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentAudioRecordCreator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 214, height: 168 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;

        case 'pronunciationAssessment':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentPronunciationAssessmentCreator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 214, height: 168 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'strokeWriting':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentStrokeWritingCreator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 400, height: 400 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'radicalWriting':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentRadicalWritingCreator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 400, height: 400 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'mc':
            // allow only one mc component
            if (prev[selectedIndex].data.some((c) => c.type === 'mc')) {
                return prev;
            }

            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentMCCreator(), componentId),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        case 'link':
            newSlideData = prev.map((s, index) => {
                if (index === selectedIndex) {
                    return {
                        ...s,
                        data: [
                            ...s.data,
                            {
                                ...CreateSlideComponent(new SlideComponentLinkCreator(), componentId),
                                ...calculateCoordinateWithSize(coordinate, { width: 200, height: 50 }),
                            },
                        ],
                    };
                }
                return s;
            });
            break;
        default:
            throw new Error(`Unknown component type: ${componentType}`);
    }

    return newSlideData;
};

const updateData = (prev: SlideData[], componentId: string, component: SlideComponent, selectedIndex: number) => {
    return prev.map((s, index) => {
        if (index === selectedIndex) {
            return {
                ...s,
                data: s.data.map((c) => {
                    if (c.id === componentId) {
                        return component;
                    }
                    return c;
                }),
            };
        }
        return s;
    });
};

const replaceSlideData = (prev: SlideData[], slideData: SlideComponent[], selectedIndex: number) => {
    return prev.map((s, index) => {
        if (index === selectedIndex) {
            return {
                ...s,
                data: [...slideData],
            };
        }
        return s;
    });
};

const removeData = (prev: SlideData[], componentId: string, selectedIndex: number) => {
    return prev.map((s, index) => {
        if (index === selectedIndex) {
            return {
                ...s,
                data: s.data.filter((component) => component.id !== componentId),
            };
        }
        return s;
    });
};

const calculateCoordinateWithSize = (coordinate: Coordinate, size: Size) => {
    if (!coordinate) {
        return { pos: { x: 0, y: 0 }, size: size };
    }

    const newCoordinate = { x: coordinate.x - size.width / 2, y: coordinate.y - size.height / 2 };

    if (CANVAS_SIZE.width - size.width < newCoordinate.x) {
        newCoordinate.x = CANVAS_SIZE.width - size.width;
    }
    if (CANVAS_SIZE.height - size.height < newCoordinate.y) {
        newCoordinate.y = CANVAS_SIZE.height - size.height;
    }
    if (newCoordinate.x < 0) {
        newCoordinate.x = 0;
    }
    if (newCoordinate.y < 0) {
        newCoordinate.y = 0;
    }

    return { pos: newCoordinate, size: size };
};

interface SlideEditorContextType {
    selectedSlide: SelectedSlideType;
    slideListData: SlideData[];
    loading: boolean;
    reorderSlide: (items: SlideData[]) => Promise<void>;
    addSlide: (data?: SlideComponent[], files?: string[]) => Promise<string | undefined>;
    duplicateSlide: (originalSlideData: SlideData) => Promise<void>;
    deleteSlide: (slideIndex: number) => Promise<void>;
}

interface ComponentEditorContextType {
    deleteComponent: (componentId: string) => void;
    updateComponent: (componentId: string, component: SlideComponent) => void;
    createComponent: (componentType: string, coordinate: Coordinate) => void;
    createSlideImage: (slideId: string, file: File) => Promise<string>;
    createSlideFile: (slideId: string, file: File) => Promise<SlideComponentFileModel | null>;
    bringToFront: (componentId: string) => void;
    bringToBack: (componentId: string) => void;
    copyComponent: (componentId: string) => void;
    pasteComponent: (componentId: string) => void;
    undo?: (componentId: string) => void;
    redo?: (componentId: string) => void;
    registerShortcutAction: (action: ShortcutAction, callback: (componentId: string) => void) => void;
    unregisterShortcutAction: (action: ShortcutAction, callback: (componentId: string) => void) => void;
}

const SlideEditorContext = React.createContext<SlideEditorContextType | undefined>(undefined);
const ComponentEditorContext = React.createContext<ComponentEditorContextType | undefined>(undefined);

type Props = {
    children: React.ReactNode | ((props: SlideEditorContextType & Pick<ComponentEditorContextType, 'createComponent' | 'createSlideFile'>) => React.ReactNode);
    lessonVersionId?: string;
    selectedSlide?: SelectedSlideType;
    setSelectedSlide?: React.Dispatch<React.SetStateAction<SelectedSlideType>>;
};

export const EditorProvider: React.FC<Props> = ({ children, lessonVersionId, selectedSlide = { index: 0, editorData: undefined, editorState: undefined }, setSelectedSlide }) => {
    const client = useApolloClient();
    const { update: updateSlides, add, remove, uploadImage, uploadFile } = useSlides();
    const { registerShortcutAction, unregisterShortcutAction } = useShortcut();
    const undoManager = useUndoManager();

    const [slideListData, setSlideListData] = useState<SlideData[]>([]);
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async () => {
        if (lessonVersionId) {
            setLoading(true);
            /**
             * Note that it is intentional not to use the `useQuery` hook here.
             * This is because we want to control the loading state and the data fetching manually.
             *
             * e.g. We don't want the selected slide to rerender when the slides are being fetched,
             *      which will cause the UI loss the UI state e.g. focused component.
             */
            const slides = await client
                .query<GetSlidesResponse>({
                    query: GET_SLIDES,
                    variables: {
                        where: {
                            lessonVersion: {
                                id: {
                                    equals: lessonVersionId,
                                },
                            },
                        },
                    },
                })
                .then((res) => res.data);
            setLoading(false);
            if (slides) {
                const data = slides.slides.map((slide) => parseSlide(slide));
                setSlideListData(data);
                setSelectedSlide?.((prev) => {
                    return {
                        index: 0,
                        editorData: data[prev.index] ? JSON.parse(JSON.stringify(data[prev.index])) : undefined,
                        editorState: undefined,
                    };
                });
            }
        }
    }, [client, lessonVersionId, setSelectedSlide]);

    const reorderSlide = useCallback(
        async (items: SlideData[], skipUndo?: boolean) => {
            const slideListDataForRevert = [...slideListData];

            const handleUpdate = async (items: SlideData[]) => {
                const newItems = items.map((item, index) => {
                    return { data: { sort: index }, where: { id: item.id } };
                });

                setSlideListData(items);

                // restore the selected slide index
                setSelectedSlide?.((prev) => {
                    const selectedSlideId = slideListData[prev.index]?.id;
                    const newIndex = items.findIndex((item) => item.id === selectedSlideId);
                    if (!items[newIndex]) {
                        return prev;
                    }
                    return { index: newIndex, editorData: items[newIndex], editorState: undefined };
                });

                await updateSlides({ data: newItems });
            };

            try {
                const previousSlideItems = slideListData;
                await handleUpdate(items);
                if (!skipUndo) {
                    undoManager.add({
                        undo: async () => {
                            await handleUpdate(previousSlideItems);
                        },
                        redo: async () => {
                            await handleUpdate(items);
                        },
                    });
                }
            } catch (error) {
                setSlideListData(slideListDataForRevert);
                throw error;
            }
        },
        [setSelectedSlide, slideListData, undoManager, updateSlides],
    );

    const addSlide = useCallback(
        async (data?: SlideComponent[], fileIds?: string[]) => {
            if (!lessonVersionId) {
                return;
            }

            const result = await add(lessonVersionId, data ? JSON.stringify(data) : '', fileIds ? fileIds : []);
            const slide = result?.[0];

            if (slide) {
                let previousSlideId: string = slide.id;

                setSlideListData((prev) => {
                    if (!prev) {
                        return prev;
                    }

                    const newSlideData: SlideData[] = [
                        ...prev,
                        {
                            id: slide.id,
                            sort: slide.sort,
                            questionCount: 0,
                            data: data ?? [],
                        },
                    ];

                    return newSlideData;
                });

                undoManager.add({
                    undo: async () => {
                        const slideObj = await remove(previousSlideId);

                        setSlideListData((prev) => {
                            const newData = prev.filter((slide) => slide.id !== slideObj?.id);

                            setSelectedSlide?.((prev) => {
                                const currentIndex = newData.findIndex((item) => item.id === newData[prev.index]?.id);

                                const newIndex = currentIndex === -1 ? prev.index - 1 : currentIndex;

                                if (!newData[newIndex]) {
                                    return prev;
                                }

                                return { index: newIndex, editorData: newData[newIndex], editorState: undefined };
                            });

                            return newData;
                        });
                    },
                    redo: async () => {
                        const result = await add(lessonVersionId, data ? JSON.stringify(data) : '', fileIds ? fileIds : []);
                        const slide = result?.[0];

                        if (slide) {
                            // Update the previousSlideId to the new slide's ID for future undo operations
                            previousSlideId = slide.id;

                            setSlideListData((prev) => {
                                if (!prev) {
                                    return prev;
                                }

                                const newSlideData: SlideData[] = [
                                    ...prev,
                                    {
                                        id: slide.id,
                                        sort: slide.sort,
                                        questionCount: 0,
                                        data: data ?? [],
                                    },
                                ];

                                return newSlideData;
                            });
                        }
                    },
                });

                return slide.id;
            }
        },
        [lessonVersionId, add, undoManager, remove, setSelectedSlide],
    );

    const duplicateSlide = useCallback(
        async (originalSlideData: SlideData) => {
            if (!lessonVersionId) {
                return;
            }

            if (!originalSlideData || !originalSlideData.id) {
                return;
            }

            // Deep clone to ensure we're not modifying the original slide data from the list
            const modifiedSlideData: SlideData = JSON.parse(JSON.stringify(originalSlideData));

            // Regenerate IDs for components and their nested elements
            modifiedSlideData.data = modifiedSlideData.data.map((component): SlideComponent => {
                const newComponentId = crypto.randomUUID();

                return {
                    ...component,
                    id: newComponentId,
                };
            });

            const result = await add(lessonVersionId, JSON.stringify(modifiedSlideData.data), []);
            const newSlide = result?.[0];

            if (newSlide) {
                let previousSlideId: string = newSlide.id;
                const previousSlideData = modifiedSlideData.data;

                setSlideListData((prev) => {
                    if (!prev) {
                        return prev;
                    }

                    // insert after the original slide
                    const originalIndex = prev.findIndex((slide) => slide.id === originalSlideData.id);
                    const newSlideParsed = parseSlide(newSlide);
                    const newSlideData: SlideData[] = [...prev];
                    newSlideData.splice(originalIndex + 1, 0, newSlideParsed);
                    // reorder the slides
                    reorderSlide(newSlideData, true);

                    return newSlideData;
                });

                undoManager.add({
                    undo: async () => {
                        const slideObj = await remove(previousSlideId);

                        setSlideListData((prev) => {
                            const newData = prev.filter((slide) => slide.id !== slideObj?.id);

                            setSelectedSlide?.((prev) => {
                                const currentIndex = newData.findIndex((item) => item.id === newData[prev.index]?.id);

                                const newIndex = currentIndex === -1 ? prev.index - 1 : currentIndex;

                                if (!newData[newIndex]) {
                                    return prev;
                                }

                                return { index: newIndex, editorData: newData[newIndex], editorState: undefined };
                            });

                            return newData;
                        });
                    },
                    redo: async () => {
                        const result = await add(lessonVersionId, previousSlideData ? JSON.stringify(previousSlideData) : '', []);
                        const newSlide = result?.[0];

                        if (newSlide) {
                            // Update the previousSlideId to the new slide's ID for future undo operations
                            previousSlideId = newSlide.id;

                            setSlideListData((prev) => {
                                if (!prev) {
                                    return prev;
                                }

                                // insert after the original slide
                                const originalIndex = prev.findIndex((slide) => slide.id === originalSlideData.id);
                                const newSlideParsed = parseSlide(newSlide);
                                const newSlideData: SlideData[] = [...prev];
                                newSlideData.splice(originalIndex + 1, 0, newSlideParsed);
                                // reorder the slides
                                reorderSlide(newSlideData, true);

                                return newSlideData;
                            });
                        }
                    },
                });
            }
        },
        [lessonVersionId, add, undoManager, reorderSlide, remove, setSelectedSlide],
    );

    const deleteSlide = useCallback(
        async (slideIndex: number) => {
            if (!slideListData || !slideListData[slideIndex]) {
                return;
            }

            if (!lessonVersionId) {
                return;
            }

            const slideID = slideListData[slideIndex].id;

            if (!slideID) {
                return;
            }

            let previousSlideId: string = slideID;
            const previousIndex = slideIndex;
            const previousData = slideListData[slideIndex].data;

            const slideObj = await remove(slideID);

            setSlideListData((prev) => {
                const newData = prev.filter((slide) => slide.id !== slideObj?.id);

                setSelectedSlide?.((prev) => {
                    const currentIndex = newData.findIndex((item) => item.id === newData[prev.index]?.id);

                    const newIndex = currentIndex === -1 ? prev.index - 1 : currentIndex;

                    if (!newData[newIndex]) {
                        return prev;
                    }

                    return { index: newIndex, editorData: newData[newIndex], editorState: undefined };
                });

                return newData;
            });

            undoManager.add({
                undo: async () => {
                    const result = await add(lessonVersionId, previousData ? JSON.stringify(previousData) : '', []);
                    const slide = result?.[0];

                    if (slide) {
                        // Update the previousSlideId to the new slide's ID for future undo operations
                        previousSlideId = slide.id;

                        setSlideListData((prev) => {
                            if (!prev) {
                                return prev;
                            }

                            const newSlideData: SlideData[] = [...prev];
                            const newSlide = {
                                id: slide.id,
                                sort: slide.sort,
                                questionCount: 0,
                                data: previousData ?? [],
                            };
                            newSlideData.splice(previousIndex, 0, newSlide);

                            reorderSlide(newSlideData, true);
                            return newSlideData;
                        });
                    }
                },
                redo: async () => {
                    const slideID = previousSlideId;
                    const slideObj = await remove(slideID);

                    setSlideListData((prev) => {
                        const newData = prev.filter((slide) => slide.id !== slideObj?.id);

                        setSelectedSlide?.((prev) => {
                            const currentIndex = newData.findIndex((item) => item.id === newData[prev.index]?.id);

                            const newIndex = currentIndex === -1 ? prev.index - 1 : currentIndex;

                            if (!newData[newIndex]) {
                                return prev;
                            }

                            return { index: newIndex, editorData: newData[newIndex], editorState: undefined };
                        });

                        return newData;
                    });
                },
            });
        },
        [add, lessonVersionId, remove, reorderSlide, setSelectedSlide, slideListData, undoManager],
    );

    const updateSingleSlideFromServer = useCallback(
        async (slide: SlideData) => {
            const newItem = [{ data: { data: JSON.stringify(slide.data) }, where: { id: slide.id } }];
            await updateSlides({ data: newItem });
        },
        [updateSlides],
    );

    const deleteComponent = useCallback(
        (componentId: string) => {
            const selectedIndex = selectedSlide.index;

            const handleUpdate = ({ prev, selectedIndex, updater }: { prev: SlideData[]; selectedIndex: number; updater: (prev: SlideData[]) => SlideData[] }) => {
                if (!prev || !prev[selectedIndex]) {
                    return prev;
                }

                const newSlideData = updater(prev);

                if (newSlideData && newSlideData[selectedIndex]) {
                    updateSingleSlideFromServer(newSlideData[selectedIndex]);
                }

                if (newSlideData[selectedIndex]) {
                    setSelectedSlide?.({
                        index: selectedIndex,
                        editorData: JSON.parse(JSON.stringify(newSlideData[selectedIndex])),
                        editorState: undefined,
                    });
                }

                return newSlideData;
            };

            let previousSlide: SlideComponent[] = [];

            setSlideListData((prev) => {
                if (!prev || !prev[selectedIndex]) {
                    return prev;
                }
                previousSlide = JSON.parse(JSON.stringify(prev[selectedIndex].data));

                return handleUpdate({
                    prev,
                    selectedIndex,
                    updater: (prevData) => removeData(prevData, componentId, selectedIndex),
                });
            });

            undoManager.add({
                undo: () => {
                    const newData = JSON.parse(JSON.stringify(previousSlide));

                    setSlideListData((prev) => {
                        if (!prev || !prev[selectedIndex]) {
                            return prev;
                        }

                        return handleUpdate({
                            prev,
                            selectedIndex,
                            updater: (prevData) => replaceSlideData(prevData, newData, selectedIndex),
                        });
                    });
                },
                redo: () => {
                    setSlideListData((prev) => {
                        if (!prev || !prev[selectedIndex]) {
                            return prev;
                        }

                        return handleUpdate({
                            prev,
                            selectedIndex,
                            updater: (prevData) => removeData(prevData, componentId, selectedIndex),
                        });
                    });
                },
            });
        },
        [selectedSlide, setSelectedSlide, undoManager, updateSingleSlideFromServer],
    );

    const updateComponent = useCallback(
        (componentId: string, component: SlideComponent) => {
            const selectedIndex = selectedSlide.index;

            const handleUpdate = ({ prev, selectedIndex, updater, rerender }: { prev: SlideData[]; selectedIndex: number; updater: (prev: SlideData[]) => SlideData[]; rerender: boolean }) => {
                if (!prev || !prev[selectedIndex]) {
                    return prev;
                }

                const newSlideData = updater(prev);

                if (newSlideData && newSlideData[selectedIndex]) {
                    updateSingleSlideFromServer(newSlideData[selectedIndex]);
                }

                if (rerender && newSlideData[selectedIndex]) {
                    setSelectedSlide?.({
                        index: selectedIndex,
                        editorData: JSON.parse(JSON.stringify(newSlideData[selectedIndex])),
                        editorState: undefined,
                    });
                }

                return newSlideData;
            };

            let previousComponent: SlideComponent | undefined;

            setSlideListData((prev) => {
                if (!prev || !prev[selectedIndex]) {
                    return prev;
                }

                previousComponent = prev[selectedIndex].data.find((c) => c.id === componentId);

                return handleUpdate({
                    prev,
                    selectedIndex,
                    updater: (prev) => updateData(prev, componentId, component, selectedIndex),
                    rerender: false,
                });
            });

            undoManager.add({
                undo: () => {
                    if (!previousComponent) return;

                    const component = previousComponent;

                    setSlideListData((prev) => {
                        if (!prev || !prev[selectedIndex]) {
                            return prev;
                        }

                        return handleUpdate({
                            prev,
                            selectedIndex,
                            updater: (prev) => updateData(prev, componentId, component, selectedIndex),
                            rerender: true,
                        });
                    });
                },
                redo: () => {
                    setSlideListData((prev) => {
                        if (!prev || !prev[selectedIndex]) {
                            return prev;
                        }

                        return handleUpdate({
                            prev,
                            selectedIndex,
                            updater: (prev) => updateData(prev, componentId, component, selectedIndex),
                            rerender: true,
                        });
                    });
                },
            });
        },
        [selectedSlide, setSelectedSlide, undoManager, updateSingleSlideFromServer],
    );

    const createComponent = useCallback(
        (componentType: string, coordinate: Coordinate) => {
            if (!componentType) {
                return;
            }

            const componentId = crypto.randomUUID();
            const selectedIndex = selectedSlide.index;

            const handleUpdate = ({ prev, selectedIndex, updater, rerender }: { prev: SlideData[]; selectedIndex: number; updater: (prev: SlideData[]) => SlideData[]; rerender: boolean }) => {
                if (!prev || !prev[selectedIndex]) {
                    return prev;
                }

                const newSlideData = updater(prev);

                // Only update if the data actually changed
                if (newSlideData && newSlideData[selectedIndex] && newSlideData !== prev) {
                    updateSingleSlideFromServer(newSlideData[selectedIndex]);
                }

                if (rerender && newSlideData[selectedIndex] && newSlideData !== prev) {
                    setSelectedSlide?.({
                        index: selectedIndex,
                        editorData: JSON.parse(JSON.stringify(newSlideData[selectedIndex])),
                        editorState: { focus: { id: componentId } },
                    });
                }

                return newSlideData;
            };

            let previousSlide: SlideComponent[] = [];

            setSlideListData((prev) => {
                if (!prev || !prev[selectedIndex]) {
                    return prev;
                }

                previousSlide = JSON.parse(JSON.stringify(prev[selectedIndex].data));

                return handleUpdate({
                    prev,
                    selectedIndex,
                    updater: (prev) => createData(prev, componentId, componentType, coordinate, selectedIndex),
                    rerender: true,
                });
            });

            undoManager.add({
                undo: () => {
                    const newData = JSON.parse(JSON.stringify(previousSlide));

                    setSlideListData((prev) => {
                        if (!prev || !prev[selectedIndex]) {
                            return prev;
                        }

                        return handleUpdate({
                            prev,
                            selectedIndex,
                            updater: (prev) => replaceSlideData(prev, newData, selectedIndex),
                            rerender: true,
                        });
                    });
                },
                redo: () => {
                    setSlideListData((prev) => {
                        if (!prev || !prev[selectedIndex]) {
                            return prev;
                        }

                        return handleUpdate({
                            prev,
                            selectedIndex,
                            updater: (prev) => createData(prev, componentId, componentType, coordinate, selectedIndex),
                            rerender: true,
                        });
                    });
                },
            });
        },
        [selectedSlide, setSelectedSlide, undoManager, updateSingleSlideFromServer],
    );

    const createSlideImage = useCallback(
        async (slideId: string, file: File): Promise<string> => {
            if (!slideId) {
                return '';
            }

            const res = await uploadImage(slideId, file);
            if (!res) {
                throw new Error('Failed to upload image');
            }
            return res.file.url;
        },
        [uploadImage],
    );

    const createSlideFile = useCallback(
        async (slideId: string, file: File): Promise<SlideComponentFileModel | null> => {
            const res = await uploadFile(slideId, file);
            if (!res) {
                throw new Error('Failed to upload image');
            }

            return { id: res.id, filename: res.file.filename, url: res.file.url };
        },
        [uploadFile],
    );

    const bringToFront = useCallback(
        (componentId: string) => {
            const selectedIndex = selectedSlide.index;

            let previousSlide: SlideComponent[] = [];
            let currentSlide: SlideComponent[] = [];

            setSlideListData((prev) => {
                if (!prev || !prev[selectedIndex]) {
                    return prev;
                }

                previousSlide = JSON.parse(JSON.stringify(prev[selectedIndex].data));

                // ignore speical components like background and dnd
                const componentType = prev[selectedIndex].data.find((item) => item.id === componentId)?.type;
                if (componentType === 'background') {
                    return prev;
                }

                const newSlideData = prev.map((s, index) => {
                    if (index === selectedIndex) {
                        const itemToMove = s.data.find((item) => item.id === componentId);
                        // background component is always on the back
                        const backgroundItem = s.data.find((item) => item.type === 'background');

                        const otherItems = s.data.filter((item) => item.id !== componentId && item.type !== 'background');

                        return {
                            ...s,
                            data: [...(backgroundItem ? [backgroundItem] : []), ...otherItems, ...(itemToMove ? [itemToMove] : [])],
                        };
                    }
                    return s;
                });

                if (newSlideData && newSlideData[selectedIndex]) {
                    updateSingleSlideFromServer(newSlideData[selectedIndex]);
                }
                if (newSlideData[selectedIndex]) {
                    setSelectedSlide?.({ index: selectedIndex, editorData: JSON.parse(JSON.stringify(newSlideData[selectedIndex])), editorState: undefined });
                }

                if (!newSlideData || !newSlideData[selectedIndex]) {
                    return prev;
                }
                currentSlide = JSON.parse(JSON.stringify(newSlideData[selectedIndex].data));

                return newSlideData;
            });

            undoManager.add({
                undo: () => {
                    const newData = JSON.parse(JSON.stringify(previousSlide));

                    setSlideListData((prev) => {
                        if (!prev || !prev[selectedIndex]) {
                            return prev;
                        }

                        const newSlideData = replaceSlideData(prev, newData, selectedIndex);

                        if (newSlideData[selectedIndex]) {
                            updateSingleSlideFromServer(newSlideData[selectedIndex]);
                            setSelectedSlide?.({
                                index: selectedIndex,
                                editorData: JSON.parse(JSON.stringify(newSlideData[selectedIndex])),
                                editorState: undefined,
                            });
                        }

                        return newSlideData;
                    });
                },
                redo: () => {
                    const newData = JSON.parse(JSON.stringify(currentSlide));

                    setSlideListData((prev) => {
                        if (!prev || !prev[selectedIndex]) {
                            return prev;
                        }

                        const newSlideData = replaceSlideData(prev, newData, selectedIndex);

                        if (newSlideData[selectedIndex]) {
                            updateSingleSlideFromServer(newSlideData[selectedIndex]);
                            setSelectedSlide?.({
                                index: selectedIndex,
                                editorData: JSON.parse(JSON.stringify(newSlideData[selectedIndex])),
                                editorState: undefined,
                            });
                        }

                        return newSlideData;
                    });
                },
            });
        },
        [selectedSlide.index, setSelectedSlide, undoManager, updateSingleSlideFromServer],
    );

    const bringToBack = useCallback(
        (componentId: string) => {
            const selectedIndex = selectedSlide.index;

            let previousSlide: SlideComponent[] = [];
            let currentSlide: SlideComponent[] = [];

            setSlideListData((prev) => {
                if (!prev || !prev[selectedIndex]) {
                    return prev;
                }

                previousSlide = JSON.parse(JSON.stringify(prev[selectedIndex].data));

                // ignore speical components like background and dnd
                const componentType = prev[selectedIndex].data.find((item) => item.id === componentId)?.type;
                if (componentType === 'background') {
                    return prev;
                }

                const newSlideData = prev.map((s, index) => {
                    if (index === selectedIndex) {
                        const itemToMove = s.data.find((item) => item.id === componentId);
                        // background component is always on the back
                        const backgroundItem = s.data.find((item) => item.type === 'background');

                        const otherItems = s.data.filter((item) => item.id !== componentId && item.type !== 'background');

                        return {
                            ...s,
                            data: [...(backgroundItem ? [backgroundItem] : []), ...(itemToMove ? [itemToMove] : []), ...otherItems],
                        };
                    }
                    return s;
                });

                if (newSlideData && newSlideData[selectedIndex]) {
                    updateSingleSlideFromServer(newSlideData[selectedIndex]);
                }
                if (newSlideData[selectedIndex]) {
                    setSelectedSlide?.({ index: selectedIndex, editorData: JSON.parse(JSON.stringify(newSlideData[selectedIndex])), editorState: undefined });
                }

                if (!newSlideData || !newSlideData[selectedIndex]) {
                    return prev;
                }
                currentSlide = JSON.parse(JSON.stringify(newSlideData[selectedIndex].data));

                return newSlideData;
            });

            undoManager.add({
                undo: () => {
                    const newData = JSON.parse(JSON.stringify(previousSlide));

                    setSlideListData((prev) => {
                        if (!prev || !prev[selectedIndex]) {
                            return prev;
                        }

                        const newSlideData = replaceSlideData(prev, newData, selectedIndex);

                        if (newSlideData[selectedIndex]) {
                            updateSingleSlideFromServer(newSlideData[selectedIndex]);
                            setSelectedSlide?.({
                                index: selectedIndex,
                                editorData: JSON.parse(JSON.stringify(newSlideData[selectedIndex])),
                                editorState: undefined,
                            });
                        }

                        return newSlideData;
                    });
                },
                redo: () => {
                    const newData = JSON.parse(JSON.stringify(currentSlide));

                    setSlideListData((prev) => {
                        if (!prev || !prev[selectedIndex]) {
                            return prev;
                        }

                        const newSlideData = replaceSlideData(prev, newData, selectedIndex);

                        if (newSlideData[selectedIndex]) {
                            updateSingleSlideFromServer(newSlideData[selectedIndex]);
                            setSelectedSlide?.({
                                index: selectedIndex,
                                editorData: JSON.parse(JSON.stringify(newSlideData[selectedIndex])),
                                editorState: undefined,
                            });
                        }

                        return newSlideData;
                    });
                },
            });
        },
        [selectedSlide, setSelectedSlide, undoManager, updateSingleSlideFromServer],
    );

    const copy = useCallback(
        async (componentId: string) => {
            const selectedIndex = selectedSlide.index;
            const selectedSlideData = slideListData[selectedIndex];
            if (!selectedSlideData) {
                return;
            }

            const component = selectedSlideData.data.find((item) => item.id === componentId);
            if (!component) {
                return;
            }

            const payload = JSON.stringify(component);

            try {
                const clipboardItemInput = new ClipboardItem({
                    [APP_CLIPBOARD_MIME]: payload,
                    ['text/plain']: component.type === 'text' && isSlideComponentText(component) ? component.content.text : ' ',
                });
                await navigator.clipboard.write([clipboardItemInput]);
            } catch (error) {
                console.error('Error copying to clipboard:', error);
            }
        },
        [selectedSlide.index, slideListData],
    );

    // Optional: paste handler that reads only our custom MIME
    const paste = useCallback(
        async (componentId: string) => {
            const selectedIndex = selectedSlide.index;
            const selectedSlideData = slideListData[selectedIndex];
            if (!selectedSlideData) {
                return;
            }

            // Resolve a reference position from the currently selected component if any
            const component = selectedSlideData.data.find((c) => c.id === componentId);

            // Helpers
            const clampPos = (pos: Coordinate, size: Size): Coordinate => {
                let x = pos.x;
                let y = pos.y;
                if (x < 0) x = 0;
                if (y < 0) y = 0;
                if (x > CANVAS_SIZE.width - size.width) x = Math.max(0, CANVAS_SIZE.width - size.width);
                if (y > CANVAS_SIZE.height - size.height) y = Math.max(0, CANVAS_SIZE.height - size.height);
                return { x, y };
            };

            // Determine final position: only offset +10,+10 if a component of the same type already exists at that exact position
            const resolvePastePos = (initialPos: Coordinate, size: Size, type: SlideComponent['type']): Coordinate => {
                const existsSameTypeSamePos = selectedSlideData.data.some((c) => c.type === type && c.pos.x === initialPos.x && c.pos.y === initialPos.y);
                const desired = existsSameTypeSamePos ? { x: initialPos.x + 10, y: initialPos.y + 10 } : initialPos;
                return clampPos(desired, size);
            };

            const insertComponent = (newComponent: SlideComponent) => {
                let previousSlide: SlideComponent[] = [];
                let currentSlide: SlideComponent[] = [];
                setSlideListData((prev) => {
                    if (!prev || !prev[selectedIndex]) return prev;

                    previousSlide = JSON.parse(JSON.stringify(prev[selectedIndex].data));

                    // Prevent duplicate singleton components
                    const singletonTypes = new Set(['background', 'dnd', 'characterCards']);
                    if (singletonTypes.has(newComponent.type as string) && prev[selectedIndex].data.some((c) => c.type === newComponent.type)) {
                        return prev; // ignore paste
                    }

                    const newSlideData = prev.map((s, idx) => {
                        if (idx !== selectedIndex) return s;
                        return { ...s, data: [...s.data, newComponent] };
                    });

                    if (newSlideData[selectedIndex]) {
                        updateSingleSlideFromServer(newSlideData[selectedIndex]);
                        setSelectedSlide?.({
                            index: selectedIndex,
                            editorData: JSON.parse(JSON.stringify(newSlideData[selectedIndex])),
                            editorState: { focus: { id: newComponent.id } },
                        });
                    }

                    if (!newSlideData || !newSlideData[selectedIndex]) {
                        return prev;
                    }
                    currentSlide = JSON.parse(JSON.stringify(newSlideData[selectedIndex].data));

                    return newSlideData;
                });

                undoManager.add({
                    undo: () => {
                        const newData = JSON.parse(JSON.stringify(previousSlide));

                        setSlideListData((prev) => {
                            if (!prev || !prev[selectedIndex]) {
                                return prev;
                            }

                            const newSlideData = replaceSlideData(prev, newData, selectedIndex);

                            if (newSlideData[selectedIndex]) {
                                updateSingleSlideFromServer(newSlideData[selectedIndex]);
                                setSelectedSlide?.({
                                    index: selectedIndex,
                                    editorData: JSON.parse(JSON.stringify(newSlideData[selectedIndex])),
                                    editorState: undefined,
                                });
                            }

                            return newSlideData;
                        });
                    },
                    redo: () => {
                        const newData = JSON.parse(JSON.stringify(currentSlide));

                        setSlideListData((prev) => {
                            if (!prev || !prev[selectedIndex]) {
                                return prev;
                            }

                            const newSlideData = replaceSlideData(prev, newData, selectedIndex);

                            if (newSlideData[selectedIndex]) {
                                updateSingleSlideFromServer(newSlideData[selectedIndex]);
                                setSelectedSlide?.({
                                    index: selectedIndex,
                                    editorData: JSON.parse(JSON.stringify(newSlideData[selectedIndex])),
                                    editorState: undefined,
                                });
                            }

                            return newSlideData;
                        });
                    },
                });
            };

            const extractJsonFromHtml = (htmlText: string): unknown | null => {
                // Parse as HTML first to get context like <meta charset="...">
                const parser = new DOMParser();
                const htmlDoc = parser.parseFromString(htmlText, APP_CLIPBOARD_MIME);

                // Try common locations where JSON may be embedded inside HTML
                const scriptJson = htmlDoc.querySelector('script[type="application/json"]')?.textContent?.trim() || '';
                const preJson = htmlDoc.querySelector('pre')?.textContent?.trim() || '';
                const bodyText = htmlDoc.body?.textContent?.trim() || '';

                const candidates = [scriptJson, preJson, bodyText, htmlText].filter(Boolean);

                for (const candidate of candidates) {
                    const trimmed = candidate.trim();
                    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                        try {
                            const result = JSON.parse(trimmed);
                            if (result) {
                                return result;
                            }
                        } catch {
                            // try next candidate
                        }
                    }
                }
                return null;
            };

            // Try custom payload first
            try {
                let parsed: unknown | null = null;
                if (typeof navigator !== 'undefined' && navigator.clipboard) {
                    if ('read' in navigator.clipboard && typeof navigator.clipboard.read === 'function') {
                        const items = await navigator.clipboard.read();
                        for (const item of items) {
                            if (item.types?.includes(APP_CLIPBOARD_MIME)) {
                                const blob = await item.getType(APP_CLIPBOARD_MIME);
                                const text = await blob.text();

                                try {
                                    parsed = extractJsonFromHtml(text);
                                } catch {
                                    parsed = null;
                                }
                                break;
                            }
                        }
                    }

                    // Fallback: readText for plain text paste
                    if (!parsed) {
                        const text = await navigator.clipboard.readText();
                        if (text && text.trim().length > 0) {
                            // Create a text component with pasted content
                            const id = crypto.randomUUID();
                            const baseSize: Size = { width: 200, height: 50 };
                            const initialPos: Coordinate = component ? { x: component.pos.x, y: component.pos.y } : { x: 20, y: 20 };
                            const basePos = resolvePastePos(initialPos, baseSize, 'text');
                            const base = CreateSlideComponent(new SlideComponentTextCreator(), id);
                            if (isSlideComponentText(base)) {
                                insertComponent({
                                    ...base,
                                    pos: basePos,
                                    size: baseSize,
                                    content: { ...base.content, text },
                                });
                            }
                            return;
                        }
                    }
                }

                // If we have a parsed component from custom payload
                if (parsed && isSlideComponent(parsed)) {
                    const copied = parsed as SlideComponent;
                    const newId = crypto.randomUUID();
                    const newSize = copied.size;
                    const initialPos: Coordinate = { x: copied.pos.x, y: copied.pos.y };
                    const basePos = resolvePastePos(initialPos, newSize, copied.type);

                    insertComponent({ ...copied, id: newId, pos: basePos });
                    return;
                }
            } catch (err) {
                console.error('Paste failed:', err);
            }
        },
        [selectedSlide.index, setSelectedSlide, slideListData, undoManager, updateSingleSlideFromServer],
    );

    // fetch the initial slides data when the component mounts
    useEffect(() => {
        refresh();
    }, [refresh]);

    // register the shortcut actions for bringToFront and bringToBack for all components
    useEffect(() => {
        registerShortcutAction('bringToFront', bringToFront);
        registerShortcutAction('bringToBack', bringToBack);
        registerShortcutAction('copy', copy);
        registerShortcutAction('paste', paste);
        registerShortcutAction('undo', undoManager.undo);
        registerShortcutAction('redo', undoManager.redo);

        return () => {
            unregisterShortcutAction('bringToFront', bringToFront);
            unregisterShortcutAction('bringToBack', bringToBack);
            unregisterShortcutAction('copy', copy);
            unregisterShortcutAction('paste', paste);
            unregisterShortcutAction('undo', undoManager.undo);
            unregisterShortcutAction('redo', undoManager.redo);
        };
    }, [bringToBack, bringToFront, copy, paste, registerShortcutAction, undoManager.redo, undoManager.undo, unregisterShortcutAction]);

    return (
        <SlideEditorContext.Provider
            value={{
                selectedSlide,
                slideListData,
                loading,
                reorderSlide,
                addSlide,
                duplicateSlide,
                deleteSlide,
            }}
        >
            <ComponentEditorContext.Provider
                value={{
                    deleteComponent,
                    updateComponent,
                    createComponent,
                    createSlideImage,
                    createSlideFile,
                    bringToFront,
                    bringToBack,
                    copyComponent: copy,
                    pasteComponent: paste,
                    undo: undoManager.canUndo ? undoManager.undo : undefined,
                    redo: undoManager.canRedo ? undoManager.redo : undefined,
                    registerShortcutAction,
                    unregisterShortcutAction,
                }}
            >
                {isFunction(children)
                    ? children({
                          selectedSlide,
                          slideListData,
                          loading,
                          reorderSlide,
                          addSlide,
                          duplicateSlide,
                          deleteSlide,
                          createComponent,
                          createSlideFile,
                      })
                    : children}
            </ComponentEditorContext.Provider>
        </SlideEditorContext.Provider>
    );
};

export const useSlideEditor = (): SlideEditorContextType => {
    const context = React.useContext(SlideEditorContext);
    if (!context) {
        throw new Error('useSlideEditor must be used within a EditorProvider');
    }

    return context;
};

export const useComponentEditor = (): ComponentEditorContextType => {
    const context = React.useContext(ComponentEditorContext);
    if (!context) {
        throw new Error('useComponentEditor must be used within a EditorProvider');
    }

    return context;
};
